import { Check } from "lucide-react";
import { type ReactNode, useMemo } from "react";

import {
  type AgendaItem,
  useListAgendaItems,
} from "@elation/api-client/src/stitch-web";

import {
  AgendaConcernHeader,
  AgendaItemEntity,
  type AgendaItemListArtifact,
  groupByConcern,
  scratchpadNotesToPlainText,
  toAgendaConcernHeader,
} from "@src/features/agenda";
import { AgendaProgressBadge } from "@src/features/agenda/ui/components/v2/AgendaProgressBadge";
import { CollapsiblePanel } from "@src/shared/components/v2/CollapsiblePanel";
import { GeneratedArtifactPanelState } from "@src/shared/components/v2/GeneratedArtifactPanelState";
import type {
  GeneratedArtifactStatus,
  GeneratedArtifactViewState,
} from "@src/shared/domain/generated-artifact";
import { cn } from "@src/shared/lib/utils";

export interface CompletedVisitAgendaPanelProps {
  encounterId?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional until Workstream A lands in the generated client. */
  artifactStatus?: GeneratedArtifactStatus;
  /** Raw `encounter.scratchpadNotes`; rendered read-only for a finished visit. */
  scratchpadNotes?: string | null;
  /** Extra header buttons, for example the dock control. */
  actions?: ReactNode;
}

export interface CompletedVisitAgendaPanelBodyProps {
  items: AgendaItem[];
  viewState?: GeneratedArtifactViewState;
  isStillGenerating?: boolean;
  scratchpadNotes?: string | null;
}

function useCompletedVisitAgendaItems({
  encounterId,
  artifactStatus: artifactStatusProp,
}: {
  encounterId?: number;
  artifactStatus?: GeneratedArtifactStatus;
}) {
  const agendaItemsQuery = useListAgendaItems(encounterId ?? 0, {
    query: {
      enabled: encounterId != null,
      refetchInterval: (query) => {
        const response = query.state.data?.data as
          | AgendaItemListArtifact
          | undefined;
        const status =
          artifactStatusProp ?? AgendaItemEntity.artifactStatus(response);
        return AgendaItemEntity.agendaItemsPollIntervalMs(status);
      },
    },
  });
  const response = agendaItemsQuery.data?.data as
    | AgendaItemListArtifact
    | undefined;
  const artifactStatus =
    artifactStatusProp ?? AgendaItemEntity.artifactStatus(response);
  const items = useMemo(
    () =>
      (response?.items ?? [])
        .filter(AgendaItemEntity.isActionable)
        .sort(AgendaItemEntity.compareByTimelineDesc),
    [response?.items],
  );

  const viewState = AgendaItemEntity.resolveViewState({
    artifactStatus,
    itemCount: items.length,
    isInitialLoading: agendaItemsQuery.isLoading,
    isFetchError: agendaItemsQuery.isError,
  });
  const isStillGenerating = AgendaItemEntity.isStillGenerating({
    artifactStatus,
    itemCount: items.length,
  });

  return {
    items,
    viewState,
    isStillGenerating,
    artifactStatus,
  };
}

export function CompletedVisitAgendaPanel({
  encounterId,
  open,
  defaultOpen = true,
  onOpenChange,
  artifactStatus: artifactStatusProp,
  scratchpadNotes,
  actions,
}: CompletedVisitAgendaPanelProps) {
  const { items, viewState, isStillGenerating } = useCompletedVisitAgendaItems({
    encounterId,
    artifactStatus: artifactStatusProp,
  });
  const addressedCount = items.filter(AgendaItemEntity.isCompleted).length;

  return (
    <CollapsiblePanel
      label="Agenda"
      count={`${addressedCount}/${items.length} addressed`}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      className="h-full"
      contentClassName="min-h-0 overflow-auto"
      actions={actions}
    >
      <CompletedVisitAgendaPanelBody
        items={items}
        viewState={viewState}
        isStillGenerating={isStillGenerating}
        scratchpadNotes={scratchpadNotes}
      />
    </CollapsiblePanel>
  );
}

/**
 * Read-only scratch pad for a finished visit. The live pad is a TipTap editor
 * (`AgendaScratchPad`); a completed visit's notes are not editable, so this
 * renders the plain-text projection instead.
 */
function CompletedVisitScratchPad({
  scratchpadNotes,
}: {
  scratchpadNotes?: string | null;
}) {
  const text = scratchpadNotesToPlainText(scratchpadNotes).trim();

  return (
    <div className="mt-3.5 border-border border-t px-3 pt-3 pb-3">
      <p className="mb-1.5 font-mono text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        Scratch pad
      </p>
      {text ? (
        <p className="min-h-[72px] whitespace-pre-wrap rounded-md border border-border bg-surface px-3 py-2 text-[13px] leading-relaxed text-foreground">
          {text}
        </p>
      ) : (
        <p className="min-h-[56px] rounded-md border border-border bg-surface px-3 py-2 text-[13px] italic leading-relaxed text-muted-foreground">
          No scratch pad notes for this visit.
        </p>
      )}
    </div>
  );
}

function AgendaItemRow({ item }: { item: AgendaItem }) {
  const isCompleted = AgendaItemEntity.isCompleted(item);

  return (
    <li
      className="flex min-h-11 items-center gap-3 px-3 py-2"
      aria-label={AgendaItemEntity.listItemLabel(item) ?? item.description}
    >
      <span
        className={cn(
          "inline-flex size-[17px] shrink-0 items-center justify-center rounded-full border border-muted-foreground/40",
          isCompleted && "border-success bg-success text-success-foreground",
        )}
        aria-hidden="true"
      >
        {isCompleted ? <Check className="size-3" /> : null}
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 text-[13px] leading-5 text-foreground",
          isCompleted && "text-muted-foreground",
        )}
      >
        {item.description}
      </span>
      <AgendaProgressBadge item={item} />
    </li>
  );
}

export function CompletedVisitAgendaPanelBody({
  items,
  viewState = "ready",
  isStillGenerating = false,
  scratchpadNotes,
}: CompletedVisitAgendaPanelBodyProps) {
  const concernGroups = useMemo(
    () =>
      groupByConcern(items).map((group) => ({
        group,
        header: toAgendaConcernHeader(group),
      })),
    [items],
  );

  const agendaState = (
    <GeneratedArtifactPanelState
      viewState={
        viewState === "ready" && items.length === 0 ? "empty" : viewState
      }
      isStillGenerating={isStillGenerating}
      generatingLabel="Generating agenda"
      generatingSkeletonWidths={["w-full", "w-[90%]"]}
      emptyMessage="No agenda items were captured for this visit."
      failedMessage="Agenda updates unavailable for this visit."
      errorFallback="Unable to load agenda."
    >
      <ul aria-label="Completed visit agenda">
        {concernGroups.map(({ group, header }) => (
          <li key={group.key}>
            <AgendaConcernHeader
              header={header}
              className="border-border border-b px-3 py-1.5"
            />
            <ul
              className="divide-y divide-border"
              aria-label={`${header.title} agenda items`}
            >
              {group.items.map((item) => (
                <AgendaItemRow key={item.id} item={item} />
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </GeneratedArtifactPanelState>
  );

  // The scratch pad is clinician-authored during the visit, not generated, so
  // it stays visible while the agenda itself is still settling, but not when
  // the fetch itself failed.
  if (viewState === "fetch-error") {
    return agendaState;
  }

  return (
    <div>
      {agendaState}
      <CompletedVisitScratchPad scratchpadNotes={scratchpadNotes} />
    </div>
  );
}
